// @flow
/** @jsx jsx */

import * as React from 'react';

import { Icon } from '@zapier/common-components';
import { marginToCss } from '@zapier/common-utils';
import { Colors, Typography } from '@zapier/theme';
import { css, jsx } from '@emotion/core';

import type { Spacing } from '@zapier/common-utils';
import type { TypographyName } from '@zapier/theme';

export type MessageBarTypes =
  | 'default'
  | 'error'
  | 'info'
  | 'plain'
  | 'success'
  | 'teams'
  | 'warning';

type MessageBarProps = {
  /** Children (usually text) to display as the main content of the message. */
  children?: React.Node,
  /** Handler for dismissing the message. */
  onDismiss?: () => void,
  margin?: Spacing,
  /** Bolded title to display in the header. */
  title?: string,
  /** The type of message bar to display. */
  type: MessageBarTypes,
  /**
   * The type (effectively the "style") of text to render. One of a list of
   * preset types, which correspond exactly to the Zapier Visual Identity:
   * https://github.com/zapier/zapier-ui/issues/36. These styles are mixed
   * in to the MessageBar styles, overriding any existing MessageBar styles
   * specified by the MessageBar type.
   */
  typography?: TypographyName,
};

type MessageListProps = {
  isOrdered: boolean,
  children: React.Node,
};

const TYPES_MAP = {
  default: {
    defaultTitle: '',
    icon: null,
    styles: {
      backgroundColor: Colors.storm,
      borderColor: Colors.batmobile,
    },
  },
  error: {
    defaultTitle: 'Bargle!',
    icon: 'error',
    styles: {
      backgroundColor: Colors.status.error.bg,
      borderColor: Colors.status.error.border,
    },
  },
  info: {
    defaultTitle: '',
    icon: null,
    styles: {
      backgroundColor: Colors.iceman,
      borderColor: Colors.captainAmerica,
    },
  },
  plain: {
    defaultTitle: '',
    icon: null,
    styles: {
      backgroundColor: Colors.status.plain.bg,
      borderColor: Colors.status.plain.border,
    },
  },
  success: {
    defaultTitle: 'Success!',
    icon: 'check',
    styles: {
      backgroundColor: Colors.status.success.bg,
      borderColor: Colors.status.success.border,
    },
  },
  teams: {
    defaultTitle: 'Team!',
    icon: 'team',
    iconStyle: {
      backgroundColor: 'transparent',
      borderRadius: 0,
      fill: Colors.mystique,
    },
    styles: {
      backgroundColor: Colors.thunder,
      borderColor: Colors.mystique,
    },
  },
  warning: {
    defaultTitle: 'Warning.',
    icon: 'alert',
    styles: {
      backgroundColor: Colors.status.warning.bg,
      borderColor: Colors.status.warning.border,
    },
  },
};

const messageBarBaseCss = (margin: ?Spacing) =>
  css({
    ...marginToCss(margin, '20px'),
    borderRadius: 3,
    borderStyle: 'solid',
    borderWidth: 1,
    color: Colors.deepSpace,
    display: 'flex',
    fontSize: 16,
    padding: 15,
    position: 'relative',
    transition: 'background-color 300ms',

    '& a': {
      color: 'inherit',
    },
  });

const messageCss = css({
  flex: 1,
  lineHeight: 1.6,
  minWidth: 0,
});

const iconBaseCss = css({
  backgroundColor: Colors.white,
  borderRadius: '50%',
  marginRight: 8,
  marginTop: 3,
});

const dismissCss = css({
  marginLeft: 8,
  marginTop: 3,
});

const titleCss = css({
  fontWeight: 600,
});

/**
 * Displays a bar with useful information for the user.
 *
 * Comes bundled with sub-components:
 * - `<MessageBar.MessageText>`: Used for displaying additional sub-text.
 * - `<MessageBar.MessageList>`: Used with lists.
 */
export const MessageBar = (props: MessageBarProps) => {
  const type = TYPES_MAP[props.type];
  const typographyStyles = Typography[props.typography] || {};

  const messageBarCss = css(
    messageBarBaseCss(props.margin),
    type.styles,
    typographyStyles
  );

  const iconCss = css(
    iconBaseCss,
    {
      color: type.styles.borderColor,
    },
    // $FlowFixMe
    type.iconStyle
  );

  // Allow user to pass title (or empty title to clear) or fall back
  // to default ttitle for type
  const title = props.title !== undefined ? props.title : type.defaultTitle;

  return (
    <div css={messageBarCss}>
      {!!type.icon && <Icon css={iconCss} name={type.icon} size={20} />}
      <div css={messageCss}>
        {title && <strong css={titleCss}>{title}</strong>} {props.children}
      </div>
      {!!props.onDismiss && (
        <Icon css={dismissCss} name="delete" onClick={props.onDismiss} />
      )}
    </div>
  );
};

MessageBar.defaultProps = {
  type: 'default',
};

const messageTextCss = css({
  lineHeight: 'inherit',
  margin: '1em 0',
  ':last-child': {
    marginBottom: 0,
  },
});

MessageBar.MessageText = props => (
  <div css={messageTextCss}>{props.children}</div>
);

const messageListCss = (isOrdered: boolean) =>
  css({
    paddingLeft: '1em',
    marginBottom: '0.5em',
    listStyleType: isOrdered ? 'decimal' : 'disc',
    ':last-child': {
      marginBottom: 0,
    },
  });

const MessageList = ({ children, isOrdered }: MessageListProps) => {
  const Tag = isOrdered ? 'ol' : 'ul';
  return <Tag css={messageListCss(isOrdered)}>{children}</Tag>;
};

MessageList.defaultProps = {
  isOrdered: false,
};

MessageBar.MessageList = MessageList;

export default MessageBar;
